import { APIProvider } from '@vis.gl/react-google-maps';
import { useFonts } from 'expo-font';
import { Stack, useGlobalSearchParams, usePathname } from 'expo-router';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import DrawerView from '@/components/drawer.web';
import Header from '@/components/header.web';
import MaintenanceOverlay from '@/components/modals/MaintenanceOverlay';
import { Sheet } from '@/components/ui/sheet';
import SplashScreenWeb from '@/components/web/SplashScreenWeb';
import { useAppReadyFunctionality } from '@/hooks/useAppReadyFunctionality';
import useAuthMiddleware from '@/hooks/useAuthMiddleware';
import { containsSection } from '@/utils/containsSection';

export default function AppLayout() {
  const pathname = usePathname();
  const { deep_link_value } = useGlobalSearchParams();

  const { showMaintenanceModal, appIsReady } = useAppReadyFunctionality(
    deep_link_value ? (deep_link_value as string | undefined) : undefined
  );

  const { hasRouted } = useAuthMiddleware();

  const [loaded, error] = useFonts({
    'CircularStd-Book': require('@/assets/fonts/CircularStd-Book.ttf'),
    'CircularStd-Medium': require('@/assets/fonts/CircularStd-Medium.ttf'),
    'CircularStd-Bold': require('@/assets/fonts/CircularStd-Bold.ttf'),
    'CircularStd-Black': require('@/assets/fonts/CircularStd-Black.ttf')
  });

  const [showSplashScreen, setShowSplashScreen] = useState(true);

  useEffect(() => {
    if ((loaded || error) && appIsReady && hasRouted && showSplashScreen) {
      setShowSplashScreen(false);
    }
  }, [loaded, error, hasRouted, appIsReady, showSplashScreen]);

  return (
    <>
      {showSplashScreen && (
        <View style={style.splash}>
          <SplashScreenWeb />
        </View>
      )}
      <MaintenanceOverlay visible={showMaintenanceModal} />
      <Sheet>
        {!containsSection(pathname, 'login') && pathname !== '/' && <Header />}
        {!containsSection(pathname, 'login') && pathname !== '/' && <DrawerView />}
        <APIProvider apiKey={process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? ''}>
          <Stack screenOptions={{ gestureEnabled: false }}>
            <Stack.Screen
              name="index"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="login"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="(bottomTabs)"
              options={{
                title: '',
                headerShown: false
              }}
            />
            <Stack.Screen
              name="filters"
              options={{
                presentation: 'modal',
                headerShown: false
              }}
            />
            <Stack.Screen
              name="npoDetail/[npoId]"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="missionDetail/[missionId]"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="notifications"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="programDetail"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="questions"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="create"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="edit"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="volunteerDetail/[volunteerId]"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="trendingDetail/[trendingId]"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="messages"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="claimNpo"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="privacyPolicy"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="termsOfService"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="donationPayment"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="paymentMethods"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="npoPaymentOnboarding"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="muxPlayer"
              options={{
                headerTintColor: 'black',
                contentStyle: {
                  backgroundColor: 'black'
                },
                headerShown: false
              }}
            />
            <Stack.Screen
              name="show"
              options={{
                headerTintColor: 'black',
                contentStyle: {
                  backgroundColor: 'black'
                },
                headerShown: false
              }}
            />
            <Stack.Screen
              name="purchaseShow"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="confirmPurchaseShow"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen
              name="confirmPurchaseGifts"
              options={{
                headerShown: false
              }}
            />
            <Stack.Screen name="+not-found" />
          </Stack>
        </APIProvider>
      </Sheet>
    </>
  );
}

const style = StyleSheet.create({
  splash: {
    position: 'absolute',
    flex: 1,
    height: '100%',
    width: '100%',
    zIndex: 9999
  }
});

export {
  // Catch any errors thrown by the Layout component, and display an error.
  ErrorBoundary
} from 'expo-router';
